// Reusable shipment status mapping for the customer-facing Track My Order page.
// Pure functions, no I/O — safe to import in server + client + tests.

export type TrackingStage =
  | "confirmed"
  | "packing"
  | "shipped"
  | "out_for_delivery"
  | "delivered";

export type TrackingExceptionType =
  | "delivery_attempt_failed"
  | "address_confirmation_required"
  | "rto_initiated"
  | "rto_in_transit"
  | "rto_delivered"
  | "cancelled"
  | "lost"
  | "tracking_unavailable";

export interface TrackingExceptionInfo {
  type: TrackingExceptionType;
  label: string;
  description: string;
}

export interface ResolvedTrackingState {
  stage: TrackingStage;
  stageIndex: number; // 0..4, aligned to STAGES order
  exception: TrackingExceptionInfo | null;
  // The normalized status string that produced this result.
  normalizedStatus: string;
}

export const TRACKING_STAGES: readonly TrackingStage[] = [
  "confirmed",
  "packing",
  "shipped",
  "out_for_delivery",
  "delivered",
] as const;

/**
 * Normalize any raw status string to a lowercase snake_case key.
 * Kept independent of shiprocket.server so tests + client can reuse it.
 */
export function normalizeTrackingStatus(s: string | null | undefined): string {
  if (!s) return "unknown";
  return String(s).trim().toLowerCase().replace(/[\s-]+/g, "_");
}

/**
 * Deterministic map from normalized status → stage or exception.
 * NEVER uses substring matching like `includes("deliver")` because that would
 * classify `undelivered` and `rto_delivered` as `delivered`.
 */
export function mapShipmentStatus(rawStatus: string | null | undefined): ResolvedTrackingState {
  const s = normalizeTrackingStatus(rawStatus);

  // Exact-match exception states first — never allow these to fall through to a stage.
  switch (s) {
    case "undelivered":
    case "ndr":
    case "delivery_attempt_failed":
    case "attempted_delivery":
      return {
        stage: "out_for_delivery",
        stageIndex: 3,
        normalizedStatus: s,
        exception: {
          type: "delivery_attempt_failed",
          label: "Delivery attempt unsuccessful",
          description:
            "The delivery attempt was unsuccessful. The courier may re-attempt delivery or request additional information.",
        },
      };
    case "address_incorrect":
    case "customer_unavailable":
    case "address_confirmation_required":
      return {
        stage: "shipped",
        stageIndex: 2,
        normalizedStatus: s,
        exception: {
          type: "address_confirmation_required",
          label: "Address or contact confirmation needed",
          description:
            "The courier needs a small confirmation on the delivery address or contact. Please reach support so we can pass it on.",
        },
      };
    case "rto_initiated":
      return {
        stage: "shipped",
        stageIndex: 2,
        normalizedStatus: s,
        exception: {
          type: "rto_initiated",
          label: "Return to origin started",
          description:
            "The courier has started returning this shipment to us. If this looks wrong, contact support right away.",
        },
      };
    case "rto_in_transit":
      return {
        stage: "shipped",
        stageIndex: 2,
        normalizedStatus: s,
        exception: {
          type: "rto_in_transit",
          label: "Returning to origin",
          description:
            "The shipment is on its way back to us. It cannot be re-attempted at your address.",
        },
      };
    case "rto_delivered":
      return {
        stage: "shipped",
        stageIndex: 2,
        normalizedStatus: s,
        exception: {
          type: "rto_delivered",
          label: "Returned to origin",
          description:
            "The package has been returned to us. Please contact support to arrange a re-ship or refund.",
        },
      };
    case "cancelled":
    case "canceled":
      return {
        stage: "confirmed",
        stageIndex: 0,
        normalizedStatus: s,
        exception: {
          type: "cancelled",
          label: "Shipment cancelled",
          description:
            "This shipment was cancelled. Reach support if you need help re-placing the order.",
        },
      };
    case "lost":
      return {
        stage: "shipped",
        stageIndex: 2,
        normalizedStatus: s,
        exception: {
          type: "lost",
          label: "Shipment reported lost",
          description:
            "The courier has flagged this shipment as lost. Please contact support so we can investigate and resolve it.",
        },
      };
  }

  // Normal happy-path stages (exact match only).
  switch (s) {
    case "new":
    case "confirmed":
    case "pending":
    case "created":
      return { stage: "confirmed", stageIndex: 0, exception: null, normalizedStatus: s };
    case "invoiced":
    case "ready_to_ship":
    case "pickup_scheduled":
    case "pickup_generated":
    case "pickup_queued":
    case "manifested":
      return { stage: "packing", stageIndex: 1, exception: null, normalizedStatus: s };
    case "picked_up":
    case "shipped":
    case "in_transit":
      return { stage: "shipped", stageIndex: 2, exception: null, normalizedStatus: s };
    case "out_for_delivery":
      return { stage: "out_for_delivery", stageIndex: 3, exception: null, normalizedStatus: s };
    case "delivered":
      return { stage: "delivered", stageIndex: 4, exception: null, normalizedStatus: s };
  }

  // Unknown → safe fallback: keep the customer at Confirmed with a soft exception.
  return {
    stage: "confirmed",
    stageIndex: 0,
    normalizedStatus: s,
    exception:
      s === "unknown"
        ? {
            type: "tracking_unavailable",
            label: "Tracking will appear here soon",
            description:
              "We haven't received a courier update yet. New events will show here automatically.",
          }
        : {
            type: "tracking_unavailable",
            label: "Tracking temporarily unavailable",
            description:
              "We couldn't classify the latest courier update. It will refresh here as new events arrive.",
          },
  };
}

/**
 * Resolve the best available shipment state from the three possible sources,
 * in priority order:
 *   1. Latest shipment.status (server-normalized).
 *   2. Latest shipment_event.status (also server-normalized).
 *   3. order.fulfillment_status (fallback only — coarse-grained).
 */
export function resolveTrackingState(sources: {
  /** Order lifecycle status — a cancelled order overrides any shipment stage. */
  orderStatus?: string | null;
  shipmentStatus?: string | null;
  latestEventStatus?: string | null;
  fulfillmentStatus?: string | null;
}): ResolvedTrackingState {
  const order = normalizeTrackingStatus(sources.orderStatus);
  if (order === "cancelled" || order === "canceled") return mapShipmentStatus("cancelled");
  if (sources.shipmentStatus && normalizeTrackingStatus(sources.shipmentStatus) !== "unknown") {
    return mapShipmentStatus(sources.shipmentStatus);
  }
  if (sources.latestEventStatus && normalizeTrackingStatus(sources.latestEventStatus) !== "unknown") {
    return mapShipmentStatus(sources.latestEventStatus);
  }
  const f = normalizeTrackingStatus(sources.fulfillmentStatus);
  switch (f) {
    case "delivered":
      return mapShipmentStatus("delivered");
    case "shipped":
    case "in_transit":
      return mapShipmentStatus("shipped");
    case "out_for_delivery":
      return mapShipmentStatus("out_for_delivery");
    case "rto":
      return mapShipmentStatus("rto_initiated");
    default:
      return mapShipmentStatus("confirmed");
  }
}

/**
 * Validate that a raw courier URL is safe to render as an external link.
 * We accept only https URLs to keep the link trusted.
 */
export function isSafeTrackingUrl(raw: string | null | undefined): boolean {
  if (!raw) return false;
  try {
    const u = new URL(String(raw));
    return u.protocol === "https:";
  } catch {
    return false;
  }
}
